import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Mail, Check } from 'lucide-react';

interface PendingReviewProps {
  user: {
    admin_verified: boolean;
    admin_rejected: boolean;
  };
  questionnaire_progress: {
    id: number;
    completed_at: string;
  };
  is_reapplication?: boolean;
}

export default function PendingReview({
  questionnaire_progress,
  is_reapplication = false,
}: PendingReviewProps) {
  const completedDate = new Date(questionnaire_progress.completed_at);
  const formattedDate = completedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Calculate days since submission
  const daysSinceSubmission = Math.floor(
    (new Date().getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  const estimatedDaysLeft = Math.max(1, 5 - daysSinceSubmission);

  return (
    <div className="min-h-screen py-8 bg-gray-50">
      <div className="max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-semibold">
                Application Status
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                  <Clock className="h-3 w-3 mr-1" />
                  Under Review
                </Badge>
                {is_reapplication && (
                  <Badge variant="outline">
                    Reapplication
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Timeline */}
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Submitted</span>
                <span className="font-medium">{completedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-between">
                  <div className="bg-white pr-2">
                    <div className="h-8 w-8 rounded-full border-2 border-primary bg-primary flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="bg-white px-2">
                    <div className="h-8 w-8 rounded-full border-2 border-primary bg-white flex items-center justify-center">
                      <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                    </div>
                  </div>
                  <div className="bg-white pl-2">
                    <div className="h-8 w-8 rounded-full border-2 border-gray-300 bg-white" />
                  </div>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium text-primary">Submitted</span>
                <span className="font-medium text-primary">Under Review</span>
                <span className="text-gray-400">Decision</span>
              </div>
            </div>

            {/* Status Message */}
            <div className="rounded-lg bg-muted p-4">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Application in Progress</p>
                  <p className="text-sm text-muted-foreground">
                    We'll notify you by email within the next 2 weeks
                  </p>
                </div>
              </div>
            </div>

            {/* Reapplication Note */}
            {is_reapplication && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-900">
                  Thank you for resubmitting with the requested updates.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
